import React, { useEffect, useState } from 'react';
import { AlertCircle, Check, DollarSign, Users, Award, Music, ArrowLeft } from 'lucide-react';
import GlowingCard from '../../../../components/ui/GlowingCard';

import { getRoutinePrice, formatCurrency, getRoutineType } from '../../../../utils/competitionUtils';

const ReviewStep = ({
  event,
  registrationData,
  pricingSettings,
  criteriaSettings,
  updateRegistrationData,
  onNext,
  onBack,
  isDarkMode,
  renderNavigation
}) => {
  // Reduced debug logging (only log once on mount)
  React.useEffect(() => {
    console.log("Review Step - Registration Data:", registrationData);
    console.log("Review Step - Pricing Settings:", pricingSettings);
  }, []); // Empty dependency array means this only runs once

  // Process workshop levels from pricing settings (same logic as dancers step)
  const workshopLevels = React.useMemo(() => {
    const registrationPricing =
      pricingSettings?.registrationPricing ||
      pricingSettings?.pricing?.registrationPricing ||
      pricingSettings?.data?.registrationPricing ||
      pricingSettings?.levels ||
      [];

    const normalizedLevels = (registrationPricing || []).map((level) => ({
      id: level.id || level.name || level.label,
      label: level.ageGroup || level.name || level.label,
      price: parseFloat(level.discountRate || level.price || level.cost || 0),
      minAge: level.minAge !== null ? parseInt(level.minAge) : null,
      maxAge: level.maxAge !== null ? parseInt(level.maxAge) : null,
    }));
    return normalizedLevels;
  }, [pricingSettings]);

  // Process competition pricing from pricing settings (same logic as competition step)
  const competitionPricingRules = React.useMemo(() => {
    const competitionPricing =
      pricingSettings?.competitionPricing ||
      pricingSettings?.pricing?.competitionPricing ||
      pricingSettings?.data?.competitionPricing ||
      [];

    // Removed debug logging to prevent console spam
    return competitionPricing || [];
  }, [pricingSettings]);
  
  // Memoize participant data to prevent re-renders
  const dancers = React.useMemo(() => 
    registrationData.regParticipants?.dancers || registrationData.dancers || registrationData.registrationDancers || [],
    [registrationData.regParticipants?.dancers, registrationData.dancers, registrationData.registrationDancers]
  );
  
  const teachers = React.useMemo(() => 
    registrationData.regParticipants?.teachers || registrationData.teachers || registrationData.registrationTeachers || [],
    [registrationData.regParticipants?.teachers, registrationData.teachers, registrationData.registrationTeachers]
  );
  
  const observers = React.useMemo(() => 
    registrationData.regParticipants?.observers || registrationData.observers || registrationData.registrationObservers || [],
    [registrationData.regParticipants?.observers, registrationData.observers, registrationData.registrationObservers]
  );
  
  // Memoize routines lookup to prevent re-renders
  const routines = React.useMemo(() => {
    const foundRoutines = registrationData.routines || registrationData.competition?.routines || [];
    
    // Minimal logging only for errors
    if (foundRoutines.length === 0 && registrationData.competition?.selectedRoutineIds) {
      console.warn("Review Step - No routines found but selectedRoutineIds exist");
    }
    
    return foundRoutines;
  }, [registrationData.routines, registrationData.competition?.routines, registrationData.competition?.selectedRoutineIds]);
  
  // Minimal logging for debugging
  React.useEffect(() => {
    console.log("Review Step - Processed Data:", {
      dancers: dancers.length,
      teachers: teachers.length,
      observers: observers.length,
      routines: routines.length
    });
  }, [dancers.length, teachers.length, observers.length, routines.length]);

  // Memoize the calculateCosts function to prevent infinite re-renders
  const calculateCosts = React.useCallback(() => {
    const costs = {
      dancers: [],
      teachers: [],
      observers: [],
      routines: []
    };
    let total = 0;

    // Dancer costs
    dancers.forEach((dancer, index) => {
      // Get dancer name
      const firstName = dancer.firstName || '';
      const lastName = dancer.lastName || '';
      
      // Create dancer cost object 
      const dancerCost = {
        name: `${firstName} ${lastName}`.trim() || 'Unknown Dancer',
        participantId: dancer.participantId || dancer.dancerId,
        items: []
      };

      // Get workshop level
      const workshopLevel = dancer.workshopLevel || dancer.options?.workshopLevel;
      
      // If we have a workshop level, add that as the primary cost
      if (workshopLevel) {
              // Get price from the dancer's pricing if available
      let workshopPrice = dancer.pricing?.basePrice || dancer.calculatedPrice || 0;
      
      // If we don't have a direct price, look it up in the processed workshop levels
      if (workshopPrice === 0) {
        const levelPricing = workshopLevels.find(level => 
          level.label === workshopLevel || level.id === workshopLevel);
        
        if (levelPricing) {
          workshopPrice = levelPricing.price || 0;
        }
      }
        
        dancerCost.items.push({
          description: `Workshop Level: ${workshopLevel}`,
          amount: workshopPrice
        });
        total += workshopPrice;
      } else {
        // Use the legacy pricing structure as a fallback
        const dancerPricing = event?.pricing?.dancer || {};
        
        // Handle legacy options
        const options = dancer.options || {};
        
        if (options.fullConvention) {
        const price = dancerPricing.fullConvention || 0;
        dancerCost.items.push({
          description: 'Full Convention',
          amount: price
        });
        total += price;
      }

        if (options.oneDayPass) {
        const price = dancerPricing.oneDayPass || 0;
        dancerCost.items.push({
          description: 'One Day Pass',
          amount: price
        });
        total += price;
      }

        if (options.competition) {
        const price = dancerPricing.competition || 0;
        dancerCost.items.push({
          description: 'Competition Fee',
          amount: price
        });
        total += price;
      }

        if (options.scholarship) {
        const price = dancerPricing.scholarship || 0;
        dancerCost.items.push({
          description: 'Scholarship',
          amount: price
        });
        total += price;
        }
      }
      
      // Add additional fees if any
      if (dancer.additionalFees && Array.isArray(dancer.additionalFees)) {
        dancer.additionalFees.forEach(fee => {
          if (fee.name && !isNaN(fee.amount)) {
            dancerCost.items.push({
              description: fee.name,
              amount: fee.amount
            });
            total += fee.amount;
          }
        });
      }
      
      // If we have source routine info, add it
      if (dancer.fromRoutine || dancer.isFromRoutine) {
        dancerCost.fromRoutine = dancer.fromRoutine || "Competition Routine";
      }

      costs.dancers.push(dancerCost);
    });

    // Teacher costs
    teachers.forEach(teacher => {
      // Get teacher name
      const firstName = teacher.firstName || '';
      const lastName = teacher.lastName || '';
      
      // Create teacher cost object
      const teacherCost = {
        name: `${firstName} ${lastName}`.trim() || 'Unknown Teacher',
        participantId: teacher.participantId || teacher.teacherId,
        items: []
      };

      // Get teacher level
      const teacherLevel = teacher.teacherLevel;
      
      // Add teacher level cost if available
      if (teacherLevel) {
        let levelPrice = 0;
        let levelLabel = teacherLevel;
        
        // Apply standard pricing based on level
        if (teacherLevel === 'studio_owner') {
          levelLabel = 'Studio Owner';
        } else if (teacherLevel === 'teacher') {
          levelLabel = 'Teacher';
        } else if (teacherLevel === 'studio_assistant') {
          levelLabel = 'Studio Assistant';
          levelPrice = 65;
        }
        
              // Override with teacher's calculated price if available
      if (teacher.pricing?.basePrice !== undefined || teacher.calculatedPrice !== undefined) {
        levelPrice = teacher.pricing?.basePrice || teacher.calculatedPrice || 0;
      }
        
        teacherCost.items.push({
          description: `Teacher Level: ${levelLabel}`,
          amount: levelPrice
        });
        total += levelPrice;
      } else {
        // Use legacy pricing as fallback
        const teacherPricing = event?.pricing?.teacher || {};
        
        // Handle legacy options
        const options = teacher.options || {};
        
        if (options.fullConvention) {
        const price = teacherPricing.fullConvention || 0;
        teacherCost.items.push({
          description: 'Full Convention',
          amount: price
        });
        total += price;
      }

        if (options.oneDayPass) {
        const price = teacherPricing.oneDayPass || 0;
        teacherCost.items.push({
          description: 'One Day Pass',
          amount: price
        });
        total += price;
      }

        if (options.observeOnly) {
        const price = teacherPricing.observeOnly || 0;
        teacherCost.items.push({
          description: 'Observe Only',
          amount: price
        });
        total += price;
      }

        if (options.training) {
        const price = teacherPricing.training || 0;
        teacherCost.items.push({
          description: 'Teacher Training',
          amount: price
        });
        total += price;
        }
      }

      costs.teachers.push(teacherCost);
    });

    // Observer costs
    observers.forEach(observer => {
      // Get observer name
      const firstName = observer.firstName || '';
      const lastName = observer.lastName || '';
      
      // Create observer cost object
      const observerCost = {
        name: `${firstName} ${lastName}`.trim() || 'Unknown Observer',
        participantId: observer.participantId || observer.observerId,
        items: []
      };
      
      // Get observer price from the most accurate source
      let observerPrice = observer.pricing?.basePrice || observer.calculatedPrice || 0;
      
      // If we don't have a direct price, use event pricing settings
      if (observerPrice === 0) {
        const observerPricing = event?.pricing?.observer || {};
        observerPrice = observerPricing.fullEvent || observerPricing.dailyRate || 0;
      }
      
      observerCost.items.push({
          description: `Observer Pass (Full Event)`,
          amount: observerPrice
      });
      
      total += observerPrice;
      costs.observers.push(observerCost);
    });

    // Routine costs
    routines.forEach((routine, index) => {
      // Create routine cost object
      const routineCost = {
        name: routine.title || 'Untitled Routine',
        routineId: routine.routineCloneId || routine.routineId,
        items: []
      };
      
      // Get routine price using the exact same function as the competition step
      let routinePrice = routine.pricing?.basePrice || routine.calculatedPrice || 0;
      
      // If we don't have a direct price, calculate it using the competition utils function
      if (routinePrice === 0) {
        routinePrice = getRoutinePrice(routine, competitionPricingRules);
      }
      
      // Use the actual user-selected type, style, and category from the competition step
      const dancerCount = Array.isArray(routine.dancers) ? routine.dancers.length : 0;
      const routineType = getRoutineType(dancerCount, competitionPricingRules) || 'Type not set';
      const userStyle = routine.style || 'Style not selected';
      const userCategory = routine.category || 'Category not selected';
      
      // Create a meaningful description using routine type, style, and category
      const description = `${routineType} - ${userStyle} - ${userCategory}`;
      
      routineCost.items.push({
        description: description,
        amount: routinePrice
      });
      
      total += routinePrice;
      costs.routines.push(routineCost);
    });

    // Removed excessive logging
    return { costs, total };
  }, [dancers, teachers, observers, routines, workshopLevels, competitionPricingRules, event?.pricing]);

  const { costs, total } = React.useMemo(() => calculateCosts(), [calculateCosts]);

  // Use the formatCurrency function from competitionUtils (already imported)

  // Update registration data with costs (memoized to prevent infinite loops)
  const previousTotal = React.useRef(registrationData.pricing?.total);
  
  useEffect(() => {
    // Check if total is a valid number and different from the previous total
    if (!isNaN(total) && total !== previousTotal.current) {
      previousTotal.current = total;
      updateRegistrationData('pricing', {
        subtotal: total,
        discounts: [],
        total: total,
        breakdown: costs // Include detailed cost breakdown
      });
    }
  }, [total, costs, updateRegistrationData]);

  return (
    <div className="min-h-full w-full">
      {/* Fixed Header */}
      <div className={`p-4 md:p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <h2 className={`responsive-heading font-bold mb-2 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          <Check className="h-6 w-6 mr-2 text-blue-500" />
          Review Registration
        </h2>
        <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Review your registration details for {event?.name}
        </p>
      </div>
      
      {/* Scrollable Content */}
      <div className="p-4 md:p-6">
        <div className="space-y-8">
          {/* Show Empty State if no data */}
          {dancers.length === 0 && teachers.length === 0 && observers.length === 0 && routines.length === 0 ? (
            <div className={`p-6 text-center rounded-lg border ${
              isDarkMode ? 'bg-gray-800/50 border-gray-700 text-gray-300' : 'bg-base-200 border-gray-200'
            }`}>
              <AlertCircle className="mx-auto h-12 w-12 mb-4 text-yellow-500" />
              <h3 className="text-lg font-medium mb-2">No Registration Data</h3>
              <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                You haven't added any participants or routines to your registration yet.
                Please go back and add dancers, teachers, observers, or routines.
              </p>
              <button
                onClick={onBack}
                className={`px-6 py-2 rounded-lg font-medium ${
                  isDarkMode 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                <ArrowLeft className="h-4 w-4 inline-block mr-1" />
                Go Back
              </button>
            </div>
          ) : (
            /* Registration Summary */
            <>
          {isDarkMode ? (
            <GlowingCard
              glowColor="from-blue-600/10 via-purple-600/10 to-violet-600/10"
              glowIntensity="blur-xl"
              cardOpacity="bg-[#1a1f2d]/90"
              className="p-6"
            >
              <RegistrationSummaryContent 
                isDarkMode={isDarkMode}
                costs={costs}
                formatCurrency={formatCurrency}
                total={total}
              />
            </GlowingCard>
          ) : (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <RegistrationSummaryContent 
                isDarkMode={isDarkMode}
                costs={costs}
                formatCurrency={formatCurrency}
                total={total}
              />
            </div>
          )}

          {/* Action Buttons */}

            </>
          )}
        
        {/* Render standardized navigation */}
        {renderNavigation && renderNavigation(onNext, onBack, true, "Complete Registration")}
        </div>
      </div>
    </div>
  );
};

// Registration summary content component
const RegistrationSummaryContent = ({
  isDarkMode,
  costs,
  formatCurrency,
  total
}) => {
  return (
    <div className="p-6 space-y-6">
      {/* Dancers Section */}
      {costs.dancers.length > 0 && (
        <div>
          <h3 className={`text-lg font-medium mb-4 flex items-center ${isDarkMode ? 'text-white' : ''}`}>
            <Users className="h-5 w-5 mr-2 text-blue-500" />
            Dancers ({costs.dancers.length})
          </h3>
          <div className="space-y-4">
            {costs.dancers.map((dancer, index) => (
              <div
                key={dancer.participantId || index}
                className={`p-4 rounded-lg ${
                  isDarkMode 
                    ? 'bg-gray-800/50 border border-white/5' 
                    : 'bg-base-200'
                }`}
              >
                <div className="flex justify-between items-start">
                  <h4 className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>
                    {dancer.name}
                    {dancer.fromRoutine && (
                      <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                        isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'
                      }`}>
                        From routine
                      </span>
                    )}
                  </h4>
                </div>
                <div className="mt-2 space-y-1">
                  {dancer.items.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className={isDarkMode ? 'text-gray-400' : 'text-base-content/70'}>
                        {item.description}
                      </span>
                      <span className={isDarkMode ? 'text-gray-400' : ''}>
                        {formatCurrency(item.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Teachers Section */}
      {costs.teachers.length > 0 && (
        <div>
          <h3 className={`text-lg font-medium mb-4 flex items-center ${isDarkMode ? 'text-white' : ''}`}>
            <Award className="h-5 w-5 mr-2 text-purple-500" />
            Teachers ({costs.teachers.length})
          </h3>
          <div className="space-y-4">
            {costs.teachers.map((teacher, index) => (
              <div
                key={teacher.participantId || index}
                className={`p-4 rounded-lg ${
                  isDarkMode 
                    ? 'bg-gray-800/50 border border-white/5' 
                    : 'bg-base-200'
                }`}
              >
                <div className="flex justify-between items-start">
                  <h4 className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>
                    {teacher.name}
                  </h4>
                </div>
                <div className="mt-2 space-y-1">
                  {teacher.items.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className={isDarkMode ? 'text-gray-400' : 'text-base-content/70'}>
                        {item.description}
                      </span>
                      <span className={isDarkMode ? 'text-gray-400' : ''}>
                        {formatCurrency(item.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Observers Section */}
      {costs.observers.length > 0 && (
        <div>
          <h3 className={`text-lg font-medium mb-4 flex items-center ${isDarkMode ? 'text-white' : ''}`}>
            <Users className="h-5 w-5 mr-2 text-teal-500" />
            Observers ({costs.observers.length})
          </h3>
          <div className="space-y-4">
            {costs.observers.map((observer, index) => (
              <div
                key={observer.participantId || index}
                className={`p-4 rounded-lg ${
                  isDarkMode 
                    ? 'bg-gray-800/50 border border-white/5' 
                    : 'bg-base-200'
                }`}
              >
                <div className="flex justify-between items-start">
                  <h4 className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>
                    {observer.name}
                  </h4>
                </div>
                <div className="mt-2 space-y-1">
                  {observer.items.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className={isDarkMode ? 'text-gray-400' : 'text-base-content/70'}>
                        {item.description}
                      </span>
                      <span className={isDarkMode ? 'text-gray-400' : ''}>
                        {formatCurrency(item.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Competition Routines Section */}
      {costs.routines.length > 0 && (
        <div>
          <h3 className={`text-lg font-medium mb-4 flex items-center ${isDarkMode ? 'text-white' : ''}`}>
            <Music className="h-5 w-5 mr-2 text-amber-500" />
            Competition Routines ({costs.routines.length})
          </h3>
          <div className="space-y-4">
            {costs.routines.map((routine, index) => (
              <div
                key={routine.routineId || index}
                className={`p-4 rounded-lg ${
                  isDarkMode 
                    ? 'bg-gray-800/50 border border-white/5' 
                    : 'bg-base-200'
                }`}
              >
                <div className="flex justify-between items-start">
                  <h4 className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>
                    {routine.name}
                  </h4>
                </div>
                <div className="mt-2 space-y-1">
                  {routine.items.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className={isDarkMode ? 'text-gray-400' : 'text-base-content/70'}>
                        {item.description}
                      </span>
                      <span className={isDarkMode ? 'text-gray-400' : ''}>
                        {formatCurrency(item.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Total Section */}
      <div className={`pt-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex justify-between items-center">
          <span className={`text-lg font-medium flex items-center ${isDarkMode ? 'text-white' : ''}`}>
            <DollarSign className="h-5 w-5 mr-1" />
            Total Registration Cost
          </span>
          <span className={`text-lg font-bold px-3 py-1 rounded-lg ${
            isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-800'
          }`}>
            {formatCurrency(total)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ReviewStep;